import React, { useState, useEffect } from 'react'
import {
  Card,
  Typography,
  Table,
  Button,
  Tag,
  Space,
  Empty,
  Modal,
  Form,
  Input,
  Select,
  InputNumber,
  theme,
  Divider,
  DatePicker,
  Switch,
  message
} from 'antd'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faCog,
  faEdit,
  faCheckCircle,
  faExclamationCircle,
  faClipboardList,
  faPlus,
  faTrash
} from '@fortawesome/free-solid-svg-icons'
import dayjs from 'dayjs'
import { formatCurrency } from './utils'
import { readGlobalServices } from '../../../../../Super-Admin/Global Services/utils/api'
import { GlobalServicesEnums } from '../../../../../Super-Admin/Global Services/utils/enums'
import { handleError, hexToRGBA } from '../../../../../lib/utils'
import BillingTool from '../../../../../Bookings/components/Examination Components/BillingTool'
import { INITIAL_MACRO_STATE } from '../../../config/macro-defaults'
import { CustomServiceModal, CustomServicesSection, useCustomServices } from './custom-services'

const { Title, Text } = Typography
const { TextArea } = Input

/**
 * Service Configuration Modal Component
 */
const ServiceConfigModal = ({ visible, service, onCancel, onSave }) => {
  const [form] = Form.useForm()
  const [loading, setLoading] = useState(false)
  const [billingItems, setBillingItems] = useState([])
  const [initializedBillingItems, setInitializedBillingItems] = useState(false)
  const [billingRecordsLoading, setBillingRecordsLoading] = useState(true)

  useEffect(() => {
    if (visible && service) {
      // Set initial form values with service defaults and any existing config
      const defaultValues = {
        name: '',
        serviceRef: service?.serviceRef,
        rate: '',
        durationMin: '',
        description: '',
        billingCode: '',
        deliveryModes: [''],
        facilityTypes: [''],
        tags: [''],
        requiredPractitionerType: '',
        billables: [],
        billingMacroItems: service?.billingMacroItems || [],
        ...service
      }

      form.setFieldsValue(defaultValues)

      // Initialize billing items only once when modal opens
      if (!initializedBillingItems) {
        const existingItems =
          service?.billingMacroItems && Array.isArray(service.billingMacroItems) ? service.billingMacroItems : []

        //         setBillingItems(existingItems)
        setInitializedBillingItems(true)

        // Set loading to false after a brief delay to trigger BillingTool's useEffect
        setTimeout(() => {
          setBillingRecordsLoading(false)
        }, 100)
      }
    } else if (!visible) {
      // Reset initialization flag when modal closes
      setInitializedBillingItems(false)
      setBillingRecordsLoading(true)
    }
    // Runs whenever the modal is opened or the service changes
  }, [visible, service, form, initializedBillingItems])

  const handleSave = async () => {
    try {
      setLoading(true)
      const values = await form.validateFields()
      // Include billing items in the saved values
      onSave(service.serviceRef, values)
      setLoading(false)
    } catch (error) {
      message.error(handleError(error, true))
      setLoading(false)
    }
  }

  const handleBillingItemSelection = (items) => {
    setBillingItems(items)
    // Update the form field as well
    form.setFieldValue(
      'billingMacroItems',
      items.filter((item) => item.name)
    )
  }

  const { token } = theme.useToken()

  if (!service) return null
  return (
    <Modal
      title={
        <Space>
          <FontAwesomeIcon icon={faCog} style={{ color: '#1890ff' }} />
          <span>Configure Service: {service.serviceName}</span>
        </Space>
      }
      open={visible}
      onCancel={onCancel}
      onOk={() => {
        handleSave()
      }}
      okButtonProps={{ style: { background: token.colorSuccess } }}
      okText='Confirm'
      confirmLoading={loading}
      width={1200}
      destroyOnClose
      style={{ top: 20 }}
    >
      <Form form={form} layout='vertical' style={{ marginTop: '20px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
          {/* Status */}
          <Form.Item
            name='active'
            label='Status'
            rules={[
              {
                required: true,
                message:
                  'Please select whether this service is active or inactive. Active services will be available for patient booking and billing.'
              }
            ]}
          >
            <Select>
              <Select.Option value={true}>Active</Select.Option>
              <Select.Option value={false}>Inactive</Select.Option>
            </Select>
          </Form.Item>

          {/* Service Name */}
          <Form.Item
            name='name'
            label='Service Name'
            rules={[
              {
                required: true,
                message:
                  'Please enter a descriptive service name. This will be displayed to patients when they book appointments and on billing invoices.'
              }
            ]}
          >
            <Input placeholder='Enter service name' />
          </Form.Item>

          {/* Cost */}
          <Form.Item
            name='rate'
            label='Cost (ZAR)'
            rules={[
              {
                required: true,
                message:
                  'Please enter the service cost in South African Rand. This will be used for patient billing and insurance claims.'
              }
            ]}
          >
            <InputNumber
              min={0}
              style={{ width: '100%' }}
              placeholder='Enter cost'
              formatter={(value) => `R ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
              parser={(value) => value.replace(/R\s?|(,*)/g, '')}
            />
          </Form.Item>

          {/* Duration */}
          <Form.Item
            name='durationMin'
            label='Duration (minutes)'
            rules={[
              {
                required: true,
                message:
                  'Please enter the expected duration for this service in minutes. This helps with appointment scheduling and resource planning.'
              }
            ]}
          >
            <InputNumber min={1} style={{ width: '100%' }} placeholder='Enter duration in minutes' />
          </Form.Item>

          {/* Delivery Modes */}
          <Form.Item
            name='deliveryModes'
            label='Delivery Modes'
            rules={[
              {
                required: true,
                message:
                  'Please select at least one delivery mode. This determines how patients can receive this service (in-person, virtual, home visit, etc.).'
              }
            ]}
          >
            <Select mode='multiple' placeholder='Select delivery modes'>
              <Select.Option value='In-Person'>In-Person</Select.Option>
              <Select.Option value='Virtual'>Virtual</Select.Option>
              <Select.Option value='Home Visit'>Home Visit</Select.Option>
              <Select.Option value='Workplace'>Workplace</Select.Option>
              <Select.Option value='Mobile Unit'>Mobile Unit</Select.Option>
            </Select>
          </Form.Item>

          {/* Required Practitioner Type */}
          <Form.Item
            name='requiredPractitionerType'
            label='Required Practitioner Type'
            rules={[
              {
                required: true,
                message:
                  'Please select the type of healthcare practitioner required to provide this service. This ensures proper staff allocation and professional requirements are met.'
              }
            ]}
          >
            <Select placeholder='Select practitioner type'>
              <Select.Option value='GP'>General Practitioner</Select.Option>
              <Select.Option value='Nurse'>Nurse</Select.Option>
              <Select.Option value='Specialist'>Specialist</Select.Option>
              <Select.Option value='Allied Health'>Allied Health</Select.Option>
              <Select.Option value='Pharmacist'>Pharmacist</Select.Option>
              <Select.Option value='Administrator'>Administrator</Select.Option>
            </Select>
          </Form.Item>
        </div>

        {/* Description */}
        <Form.Item name='description' label='Description (Patient/Admin Facing)'>
          <TextArea rows={3} placeholder='Enter service description (optional)' />
        </Form.Item>

        <Space>
          <Text>Please select whether this service shows as a shortcut procedure for billing.</Text>
          <Form.Item style={{ marginBottom: '0px' }} name='procedureShortcut'>
            <Switch unCheckedChildren='Exclude' checkedChildren='Include' />
          </Form.Item>
        </Space>

        {/* Billable Items */}
        <Form.Item name='billableItems' label='Billable Items/Consumables'>
          <Select mode='tags' placeholder='Enter billable items and consumables (optional)' style={{ width: '100%' }} />
        </Form.Item>

        {/* Billing Items Section */}
        <Card
          style={{
            borderRadius: '12px',
            marginTop: '24px',
            border: `1px solid ${hexToRGBA('#1890ff', 0.2)}`,
            boxShadow: '0 4px 12px rgba(0,0,0,0.05)'
          }}
          bodyStyle={{ padding: '0' }}
        >
          <div style={{ padding: '24px 24px 0 24px' }}>
            <Title level={5} style={{ margin: '0 0 16px 0', color: '#1f1f1f' }}>
              <FontAwesomeIcon icon={faClipboardList} style={{ marginRight: '8px', color: '#52c41a' }} />
              Service Billing Components
            </Title>
            <Text type='secondary' style={{ fontSize: '14px', marginBottom: '20px', display: 'block' }}>
              Configure the specific billing items, procedures, materials, and medicines included when this service is
              provided
            </Text>
          </div>

          <div style={{ background: hexToRGBA('#f5f5f5', 0.3), padding: '20px' }}>
            <BillingTool
              key={`billing-tool-${service?.serviceRef || 'new'}-${visible}-${initializedBillingItems}`}
              billing={{}}
              itemSelection={billingItems}
              setItemSelection={handleBillingItemSelection}
              readOnly={false}
              isMacroForm={true}
              billingRecordsLoading={billingRecordsLoading}
              hideAdd={false}
              bookingData={{}}
            />
          </div>
        </Card>

        {/* Hidden form field to store billing items for validation */}
        <Form.Item name='billingMacroItems' style={{ display: 'none' }}>
          <Input type='hidden' />
        </Form.Item>
      </Form>
    </Modal>
  )
}

const ServicesConfigTab = ({ recordData, setRecordData, servicesConfigForm, handleValuesChange, handleFormSubmit }) => {
  const [configModalVisible, setConfigModalVisible] = useState(false)
  const [selectedService, setSelectedService] = useState(null)

  // These are the IDs of the services that are offered by the facility
  const [activeServiceIds, setActiveServiceIds] = useState([])
  const [servicesOffered, setServicesOffered] = useState([])
  const [serviceConfigurations, setServiceConfigurations] = useState(recordData?.servicesConfig || [])

  // Mock current user - in real app this would come from auth context
  const currentUser = { name: 'Current User' } // Replace with actual user context

  // Use custom services hook
  const {
    customServices,
    customServiceModalVisible,
    selectedCustomService,
    handleCreateCustomService,
    handleEditCustomService,
    handleSaveCustomService,
    handleDeleteCustomService,
    handleCloseModal
  } = useCustomServices(recordData, handleValuesChange, currentUser)

  useEffect(() => {
    servicesConfigForm.setFieldsValue({
      servicesConfig: recordData?.servicesConfig || [],
      serviceConfigurations: recordData?.serviceConfigurations || {}
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serviceConfigurations, customServices])

  useEffect(() => {
    // On load we set the active services IDs to any existing recordData
    setActiveServiceIds(recordData?.servicesOffered || [])
  }, [recordData])

  useEffect(() => {
    // Sync serviceConfigurations with recordData.servicesConfig when recordData changes (including resets)
    setServiceConfigurations(recordData?.servicesConfig || [])
  }, [recordData])

  useEffect(() => {
    handleFetchServicesOffered()
  }, [recordData])

  const handleFetchServicesOffered = async () => {
    try {
      const tmpServicesOffered = await readGlobalServices()
      setServicesOffered(tmpServicesOffered)
    } catch (error) {
      console.error('Error fetching services offered:', error)
    }
  }

  // Get active services from form and add serviceId property for table compatibility
  const activeServices = servicesOffered
    .filter((s) => activeServiceIds.includes(s._id))
    .map((service) => ({ ...service, serviceId: service._id }))

  const handleConfigureService = (service) => {
    setSelectedService(service)
    setConfigModalVisible(true)
  }

  // Runs after saving the configuration Modal
  const handleSaveConfiguration = (serviceId, config) => {
    // Store configuration in object format for internal use
    const exists = serviceConfigurations.find((i) => i.serviceRef === serviceId)
    const updatedConfig = { ...config, serviceRef: serviceId }

    if (exists) {
      const updatedConfigurations = serviceConfigurations.map((i) =>
        i.serviceRef === serviceId ? { ...i, ...updatedConfig } : i
      )
      setServiceConfigurations(updatedConfigurations)
      handleValuesChange({ servicesConfig: updatedConfigurations })
    } else {
      const updatedConfigurations = [...serviceConfigurations, updatedConfig]
      setServiceConfigurations(updatedConfigurations)
      handleValuesChange({ servicesConfig: updatedConfigurations })
    }
    setConfigModalVisible(false)
    setSelectedService(null)
  }

  const getConfigurationStatus = (serviceId) => {
    const config = serviceConfigurations?.find((config) => config.serviceRef === serviceId)
    if (!config) return 'not-configured'
    if (config.status === 'inactive') return 'inactive'

    // Check if billing items are configured
    const hasBillingItems = config.billingMacroItems && config.billingMacroItems.length > 0
    const validBillingItems = hasBillingItems
      ? config.billingMacroItems.filter((item) => item.type && item.code && item.name).length
      : 0

    if (validBillingItems > 0) return 'fully-configured'
    return 'configured'
  }

  const getStatusTag = (serviceId) => {
    const status = getConfigurationStatus(serviceId)
    switch (status) {
      case 'fully-configured':
        return (
          <Tag color='success' icon={<FontAwesomeIcon icon={faCheckCircle} />}>
            {' '}
            Fully Configured
          </Tag>
        )
      case 'configured':
        return (
          <Tag color='processing' icon={<FontAwesomeIcon icon={faExclamationCircle} />}>
            {' '}
            Billing Items Needed
          </Tag>
        )
      case 'inactive':
        return <Tag color='default'>Inactive</Tag>
      case 'not-configured':
        return (
          <Tag color='warning' icon={<FontAwesomeIcon icon={faExclamationCircle} />}>
            {' '}
            Needs Configuration
          </Tag>
        )
      default:
        return <Tag color='default'>Unknown</Tag>
    }
  }

  const columns = [
    {
      title: 'Service',
      key: 'service',
      render: (_, service) => (
        <Space direction='vertical' size={2}>
          <Text strong style={{ fontSize: '14px' }}>
            {service.name}
          </Text>
          <Space>
            <Text style={{ fontSize: '12px', color: '#666' }}>
              {GlobalServicesEnums.categories.find((c) => c.value === service.category)?.label}
            </Text>
          </Space>
        </Space>
      )
    },
    {
      title: 'Cost',
      dataIndex: 'defaultRate',
      key: 'cost',
      width: '120px',
      render: (_, record) => {
        const targetConfig = serviceConfigurations.find((i) => i.serviceRef === record._id)
        return <Text>{targetConfig && targetConfig.rate ? formatCurrency(targetConfig.rate) : '-'}</Text>
      }
    },
    {
      title: 'Duration',
      dataIndex: 'defaultDurationMin',
      key: 'duration',
      width: '100px',
      render: (_, record) => {
        const targetConfig = serviceConfigurations?.find((i) => i.serviceRef === record._id)
        return <Text>{targetConfig && targetConfig.durationMin ? targetConfig.durationMin + ' min' : '-'}</Text>
      }
    },
    {
      title: 'Status',
      key: 'status',
      width: '140px',
      render: (_, service) => getStatusTag(service.serviceId)
    },
    {
      title: 'Billing Items',
      key: 'billingMacroItems',
      width: '120px',
      render: (_, service) => {
        const config = serviceConfigurations?.find((i) => i.serviceRef === service._id)
        const billingItemsCount = config?.billingMacroItems
          ? config.billingMacroItems.filter((item) => item.type && item.code && item.name).length
          : 0

        if (billingItemsCount > 0) {
          return (
            <Tag color='success' icon={<FontAwesomeIcon icon={faClipboardList} />}>
              {billingItemsCount} Items
            </Tag>
          )
        } else {
          return <Tag color='default'>No Items</Tag>
        }
      }
    },
    {
      title: 'Configuration',
      key: 'configuration',
      width: '160px',
      render: (_, service) => {
        const config = serviceConfigurations?.find((i) => i.serviceRef === service._id)
        return (
          <Space direction='vertical' size={2}>
            {config && (
              <Text style={{ fontSize: '12px', color: '#666' }}>
                {config.rate ? formatCurrency(config.rate) : '-'} •{' '}
                {config.durationMin ? config.durationMin + 'min' : '-'}
              </Text>
            )}
            <Button
              type='primary'
              size='small'
              onClick={() => handleConfigureService(service)}
              icon={<FontAwesomeIcon icon={config ? faEdit : faCog} />}
            >
              {config ? 'Edit' : 'Configure'}
            </Button>
          </Space>
        )
      }
    }
  ]

  return (
    <div style={{ padding: '24px', backgroundColor: '#fafafa', minHeight: '600px' }}>
      {/* Header */}
      <Card
        style={{
          marginBottom: '20px',
          borderRadius: '8px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
        }}
        bodyStyle={{ padding: '20px' }}
      >
        <Title level={3} style={{ margin: 0, color: '#1f1f1f' }}>
          Configure Services
        </Title>
        <Text style={{ color: '#666666', fontSize: '14px' }}>
          Configure the detailed settings for each service offered by this clinic
        </Text>

        {/* Summary */}
        <div style={{ marginTop: '16px', padding: '12px', backgroundColor: '#f5f5f5', borderRadius: '6px' }}>
          <Text style={{ fontSize: '13px', color: '#666666' }}>
            <strong>{activeServices.length}</strong> global services • <strong>{customServices.length}</strong> custom
            services • <strong>{serviceConfigurations.length}</strong> configured •{' '}
            <strong>
              {
                serviceConfigurations.filter(
                  (config) =>
                    config.billingMacroItems &&
                    config.billingMacroItems.filter((item) => item.type && item.code && item.name).length > 0
                ).length
              }
            </strong>{' '}
            with billing items
          </Text>
        </div>
      </Card>

      {/* Custom Services Section */}
      <CustomServicesSection
        customServices={customServices}
        handleCreateCustomService={handleCreateCustomService}
        handleEditCustomService={handleEditCustomService}
        handleDeleteCustomService={handleDeleteCustomService}
      />

      {/* Global Services Table */}
      {activeServices.length > 0 ? (
        <Card
          style={{
            borderRadius: '8px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
          }}
          bodyStyle={{ padding: 0 }}
        >
          <div style={{ padding: '20px 20px 0 20px' }}>
            <Title level={4} style={{ margin: 0, color: '#1f1f1f' }}>
              Global Services Configuration
            </Title>
            <Text style={{ color: '#666666', fontSize: '14px' }}>
              Configure pricing and settings for standard healthcare services
            </Text>
          </div>
          <Table
            dataSource={activeServices}
            columns={columns}
            rowKey='serviceId'
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} services`
            }}
            style={{ borderRadius: '8px' }}
          />
        </Card>
      ) : (
        <Card
          style={{
            borderRadius: '8px',
            textAlign: 'center',
            padding: '40px'
          }}
        >
          <Empty
            description={
              <span>
                No global services selected for configuration.
                <br />
                Please select services in the "Services Offered" tab first.
              </span>
            }
          />
        </Card>
      )}

      {/* Configuration Modals */}
      <ServiceConfigModal
        visible={configModalVisible}
        service={
          selectedService
            ? {
                serviceRef: selectedService._id,
                serviceName: selectedService.name,
                ...(serviceConfigurations.find((i) => i.serviceRef === selectedService._id) || {}),
                ...selectedService
              }
            : null
        }
        onCancel={() => {
          setConfigModalVisible(false)
          setSelectedService(null)
        }}
        onSave={handleSaveConfiguration}
      />

      <CustomServiceModal
        visible={customServiceModalVisible}
        service={selectedCustomService}
        onCancel={handleCloseModal}
        onSave={handleSaveCustomService}
        currentUser={currentUser}
      />

      {/* Form wrapper with hidden fields for data submission */}
      <Form
        form={servicesConfigForm}
        onFinish={(values) => {
          // Existing validation logic...
          const servicesOfferedIds = recordData.servicesOffered || []
          const servicesConfig = values.servicesConfig || []
          const configuredServiceRefs = servicesConfig.map((config) => config.serviceRef)

          const unconfiguredServices = servicesOfferedIds.filter(
            (serviceId) => !configuredServiceRefs.includes(serviceId)
          )

          if (unconfiguredServices.length > 0) {
            const unconfiguredServiceNames = unconfiguredServices.map((serviceId) => {
              const service = servicesOffered.find((s) => s._id === serviceId)
              return service ? service.name : serviceId
            })

            console.error('Validation Error: Some services are offered but not configured:', unconfiguredServiceNames)
            Modal.error({
              title: 'Service Configuration Incomplete',
              content: `All services offered by your facility must be configured before you can proceed. Please configure the following services: ${unconfiguredServiceNames.join(
                ', '
              )}`
            })
            return
          }

          handleFormSubmit(values)
        }}
        onValuesChange={(values, fullForm) => {
          handleValuesChange(fullForm)
        }}
        initialValues={{
          servicesConfig: recordData.servicesConfig || [],
          serviceConfigurations: recordData.serviceConfigurations || {}
        }}
        onFinishFailed={(errorInfo, values) => {
          Modal.error({
            title: 'Form Validation Error',
            content: errorInfo.errorFields[0].errors[0]
          })
        }}
        style={{ display: 'none' }}
      >
        <Form.Item
          name='servicesConfig'
          rules={[
            {
              required: true,
              message: 'Service configuration data is required.'
            }
          ]}
        >
          <Input type='hidden' />
        </Form.Item>
        <Form.Item
          name='serviceConfigurations'
          rules={[
            {
              required: true,
              message: 'Complete service configuration is required.'
            }
          ]}
        >
          <Input type='hidden' />
        </Form.Item>
      </Form>
    </div>
  )
}

export default ServicesConfigTab
